import { createI18n } from 'vue-i18n'
import insane from 'insane'
import { marked } from 'marked'

import Preferences from '@/plugins/preferences'
import bus from '@/plugins/eventbus'
import { INSANE_CONFIG, getBrowserLocale, getSupportedLocales } from '@/utils/i18n'

const supportedLocales = getSupportedLocales()

function getStartingLocale() {
  const userLocale = getBrowserLocale({ countryCodeOnly: true })
  if (Object.keys(supportedLocales).includes(userLocale)) return userLocale
  return 'en'
}

const startingLocale = getStartingLocale()

// Create i18n instance with Vue 3 in legacy mode for compatibility
const i18n = createI18n({
  legacy: true, // Use legacy mode to maintain Vue 2 compatibility
  locale: startingLocale,
  fallbackLocale: 'en',
  messages: {},
})

function setI18nLanguage(lang) {
  i18n.global.locale = lang
  document.querySelector('html').setAttribute('lang', lang)
  return lang
}

export async function loadAsyncLocale(locale) {
  const { availableLocales } = i18n.global

  if (availableLocales.length > 0 && i18n.global.locale === locale) {
    return setI18nLanguage(locale)
  }

  if (availableLocales.includes(locale)) {
    return setI18nLanguage(locale)
  }

  const messages = await import(
    /* webpackChunkName: "[request].lang" */
    `@/lang/${locale}.json`
  )

  i18n.global.setLocaleMessage(locale, messages.default)
  return setI18nLanguage(locale)
}

// Helper for markdown translation
export function mdt(path, ...args) {
  return insane(
    marked.parseInline(i18n.global.t(path, ...args), { gfm: false }), // gfm: false to disable autolinking in translations
    INSANE_CONFIG,
  )
}

// Setup function for Vue 3
export const setupI18n = app => {
  // Add i18n instance to app
  app.use(i18n)

  // Add i18n to globalProperties to maintain Vue 2 compatibility
  app.config.globalProperties.$i18n = i18n.global

  // Add tc (translate with pluralization) method to globalProperties for backward compatibility
  app.config.globalProperties.$tc = (key, count, values) => {
    return i18n.global.t(key, values ? { ...values, count } : { count }, count)
  }

  // Add tc function to the global i18n instance for Composition API compatibility
  i18n.global.tc = (key, count, values) => {
    return i18n.global.t(key, values ? { ...values, count } : { count }, count)
  }

  // Add markdown translation helper
  app.config.globalProperties.$mdt = (path, ...args) => insane(
    marked.parseInline(i18n.global.t(path, ...args), { gfm: false }), // gfm: false to disable autolinking in translations
    INSANE_CONFIG,
  )

  // Add helper properties and methods that were previously on VueI18n prototype
  i18n.global.supportedLocales = Object.keys(supportedLocales)
  i18n.global.loadAsyncLocale = loadAsyncLocale
  i18n.global.mdt = app.config.globalProperties.$mdt

  // Listen for app creation to load user preferred locale
  bus.on('app:created', async appInstance => {
    const userPreferredLocale = await Preferences.get('lang')
    appInstance.config.globalProperties.$store.dispatch('device/loadLocale', userPreferredLocale || startingLocale)
  })
}

export default i18n

// https://phrase.com/blog/posts/ultimate-guide-to-vue-localization-with-vue-i18n
